import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, getDoc, deleteDoc, addDoc, setDoc, Timestamp } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { formatDisplayDate, formatDate, convertFromISODate, convertToISODate } from '../utils/dateUtils';
import { TABLE_ICONS } from '../utils/tableIcons';
import Snackbar from '../components/Snackbar';
import { useSnackbar } from '../hooks/useSnackbar';
import './Users.css';
import '../components/UserFilters.css';
import { exportUsersToExcel } from '../utils/exportToExcel';

const Users = () => {
  const navigate = useNavigate();
  const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(10);
  const [formData, setFormData] = useState({
    name: '',
    mobileNumber: '',
    address: '',
    pincode: '',
    birthDate: '',
    birthday: '',
    profileImageUrl: ''
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  // Fetch subscriptions after users are loaded (lazy loading)
  useEffect(() => {
    if (users.length > 0) {
      fetchSubscriptionsForUsers();
    }
  }, [users.length]);

  const fetchUsers = async () => {
    try {
      setLoading(true);

      const querySnapshot = await getDocs(collection(db, 'users'));
      
      const usersList = querySnapshot.docs.map(docSnap => {
        const data = docSnap.data();
        const phoneNumber = data.mobileNumber || docSnap.id;
        
        return {
          id: docSnap.id,
          phoneNumber: phoneNumber,
          name: data.name || '',
          mobileNumber: data.mobileNumber || '',
          address: data.address || '',
          pincode: data.pincode || null,
          activePlan: '...', // Will be loaded lazily
          birthDate: formatDisplayDate(data.birthDate) || '',
          birthday: formatDisplayDate(data.birthday) || '',
          profileImageUrl: data.profileImageUrl || null,
          createdAt: data.createdAt || null,
          updatedAt: data.updatedAt || ''
        };
      });
      
      // Sort by name
      usersList.sort((a, b) => a.name.localeCompare(b.name));

      setUsers(usersList);
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch subscriptions for all users in background (lazy loading)
  const fetchSubscriptionsForUsers = async () => {

    const updatedUsers = await Promise.all(
      users.map(async (user) => {
        try {
          const phoneNumber = user.phoneNumber || user.mobileNumber;
          const subscriptionDoc = await getDoc(doc(db, 'users', phoneNumber, 'subscription', 'plan'));
          
          if (subscriptionDoc.exists()) {
            const subData = subscriptionDoc.data();
            const now = new Date();
            const expiryDate = subData.expiryDate?.toDate();
            const isActive = expiryDate && now <= expiryDate;
            
            return {
              ...user,
              activePlan: isActive ? subData.planName : 'Expired'
            };
          }
          
          return {
            ...user,
            activePlan: 'N/A'
          };
        } catch (error) {
          
          return {
            ...user,
            activePlan: 'N/A'
          };
        }
      })
    );
    
    setUsers(updatedUsers);
    
  };

  const handleAddUser = async () => {
    try {
      if (!formData.name || !formData.mobileNumber) {
        showSnackbar('Please fill in required fields (Name and Mobile Number)', 'warning');
        return;
      }

      const userData = {
        name: formData.name,
        mobileNumber: formData.mobileNumber,
        address: formData.address,
        pincode: formData.pincode,
        birthDate: formData.birthDate ? convertFromISODate(formData.birthDate) : '',
        birthday: formData.birthday,
        profileImageUrl: formData.profileImageUrl,
        createdAt: new Date(),
        updatedAt: formatDate(new Date())
      };

      // Create user with auto-generated document ID
      await addDoc(collection(db, 'users'), userData);
      
      // Automatically apply Free Trial plan
      const now = new Date();
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + 7); // 7 days for Free Trial
      
      const freeTrialData = {
        planName: 'Free Trial',
        planCount: 1,
        price: 0,
        duration: 7,
        remainingDays: 7,
        startDate: Timestamp.fromDate(now),
        expiryDate: Timestamp.fromDate(expiryDate),
        status: 'active',
        appliedBy: 'admin',
        appliedAt: Timestamp.fromDate(now),
        updatedAt: Timestamp.fromDate(now),
        lastChecked: Timestamp.fromDate(now),
        isExtended: false,
        lastExtendedPlan: null
      };
      
      // Store Free Trial subscription
      await setDoc(
        doc(db, 'users', formData.mobileNumber, 'subscription', 'plan'),
        freeTrialData
      );

      await fetchUsers();
      setShowAddForm(false);
      setFormData({
        name: '',
        mobileNumber: '',
        address: '',
        pincode: '',
        birthDate: '',
        birthday: '',
        profileImageUrl: ''
      });
      
      showSnackbar('User added successfully with Free Trial plan!', 'success');
      
    } catch (error) {
      console.error('Error adding user:', error);
      showSnackbar('Error adding user. Please try again.', 'error');
    }
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    try {
      setDeletingUserId(userToDelete.id);

      // First, get the user's mobile number to find related profiles
      const userDocRef = doc(db, 'users', userToDelete.id);
      const userDocSnap = await getDoc(userDocRef);
      let userMobileNumber = null;
      
      if (userDocSnap.exists()) {
        const userData = userDocSnap.data();
        userMobileNumber = userData.mobileNumber;
        
      }

      // Delete subcollections if mobile number exists
      if (userMobileNumber) {
        try {
          // Delete business profile
          
          await deleteDoc(doc(db, 'users', userMobileNumber, 'business', 'profile'));
          
        } catch (error) {
          
        }

        try {
          // Delete political profile
          
          await deleteDoc(doc(db, 'users', userMobileNumber, 'political', 'profile'));
          
        } catch (error) {
          
        }

        try {
          // Delete custom frames
          
          const customFramesSnapshot = await getDocs(collection(db, 'users', userMobileNumber, 'customFrames'));
          for (const frameDoc of customFramesSnapshot.docs) {
            const frameData = frameDoc.data();
            // Delete from Storage
            if (frameData.storagePath) {
              try {
                await deleteObject(ref(storage, frameData.storagePath));
                
              } catch (storageError) {
                
              }
            }
            // Delete from Firestore
            await deleteDoc(doc(db, 'users', userMobileNumber, 'customFrames', frameDoc.id));
          }
          
        } catch (error) {
          
        }

        // Also try to delete any documents that might use mobile number as document ID
        try {
          
          await deleteDoc(doc(db, 'users', userMobileNumber));
          
        } catch (error) {
          
        }
      }

      // Delete custom frames for the main user document ID
      try {
        
        const customFramesSnapshot = await getDocs(collection(db, 'users', userToDelete.id, 'customFrames'));
        for (const frameDoc of customFramesSnapshot.docs) {
          const frameData = frameDoc.data();
          // Delete from Storage
          if (frameData.storagePath) {
            try {
              await deleteObject(ref(storage, frameData.storagePath));
              
            } catch (storageError) {
              
            }
          }
          // Delete from Firestore
          await deleteDoc(doc(db, 'users', userToDelete.id, 'customFrames', frameDoc.id));
        }
        
      } catch (error) {
        
      }

      // Delete the main user document
      
      await deleteDoc(doc(db, 'users', userToDelete.id));

      // Refresh the users list
      await fetchUsers();
      
      // Close modal and reset state
      setShowDeleteConfirm(false);
      setUserToDelete(null);
      
      showSnackbar('User and all associated data deleted successfully!', 'success');

    } catch (error) {
      console.error('Error deleting user:', error);
      showSnackbar(`Error deleting user: ${error.message}. Please try again.`, 'error');
    } finally {
      setDeletingUserId(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setUserToDelete(null);
  };

  const handleEditUser = (user) => {
    // Use phoneNumber for the route parameter, fallback to id if phoneNumber is not available
    const routeParam = user.phoneNumber || user.mobileNumber || user.id;
    navigate(`/admin/user-profile/${routeParam}`);
  };

  // Filter users based on search term and date range
  const filteredUsers = users.filter(user => {
    const matchesSearch =
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.mobileNumber.includes(searchTerm) ||
      user.address.toLowerCase().includes(searchTerm.toLowerCase());
    if (!matchesSearch) return false;
    if (startDate || endDate) {
      let created = user.createdAt;
      if (created && typeof created === 'object' && created.toDate) {
        created = created.toDate();
      } else if (typeof created === 'string' || created instanceof String) {
        created = new Date(created);
      }
      if (startDate && created < new Date(startDate)) return false;
      if (endDate && created > new Date(endDate + 'T23:59:59')) return false;
    }
    return true;
  });

  // Pagination
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) {
    return (
      <div className="users-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="users-page">
      <div className="page-header users-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div className="header-left">
          <h2>Users</h2>
        </div>
        <div className="users-header-actions">
          <div className="count-badge">
            <span className="count-label">TOTAL USERS</span>
            <span className="count-number">{users.length}</span>
          </div>
          <div className="search-container">
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </div>
      </div>

      {/* Date filter and export button above the table, styled visually */}
      <div className="user-filters-row user-filters-row-inline">
        <label className="user-filter-label-inline">Start Date:
          <input
            type="date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            className="user-filter-date"
          />
        </label>
        <label className="user-filter-label-inline">End Date:
          <input
            type="date"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            className="user-filter-date"
          />
        </label>
        {(startDate || endDate) && (
          <button
            className="user-filter-clear-btn"
            onClick={() => { setStartDate(''); setEndDate(''); }}
            title="Clear date filter"
            type="button"
          >
            ×
          </button>
        )}
        <button
          className="user-export-btn user-export-btn-compact"
          onClick={() => exportUsersToExcel(filteredUsers, 'users.xlsx')}
        >
          Export
        </button>
      </div>

      {/* Add User Form Modal */}
      {showAddForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Add New User</h3>
              <button 
                onClick={() => setShowAddForm(false)}
                className="close-btn"
              >
                ×
              </button>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); handleAddUser(); }} className="user-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    placeholder="Enter full name"
                  />
                </div>
                <div className="form-group">
                  <label>Mobile Number *</label>
                  <input
                    type="tel"
                    value={formData.mobileNumber}
                    onChange={(e) => setFormData({...formData, mobileNumber: e.target.value})}
                    required
                    placeholder="Enter mobile number"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Address</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  placeholder="Enter address (e.g., guhagar,chiplun,ratnagiri)"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Pincode</label>
                  <input
                    type="text"
                    value={formData.pincode}
                    onChange={(e) => setFormData({...formData, pincode: e.target.value})}
                    placeholder="Enter pincode"
                  />
                </div>
                <div className="form-group">
                  <label>Birth Date</label>
                  <input
                    type="date"
                    value={convertToISODate(formData.birthDate)}
                    onChange={(e) => setFormData({...formData, birthDate: convertFromISODate(e.target.value)})}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Birthday (Display Format)</label>
                <input
                  type="text"
                  value={formData.birthday}
                  onChange={(e) => setFormData({...formData, birthday: e.target.value})}
                  placeholder="Enter birthday (e.g., 22-11-2003)"
                />
              </div>
              <div className="form-actions">
                <button type="button" onClick={() => setShowAddForm(false)} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Add User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>Sr. No</th>
              <th>Name</th>
              <th>Mobile Number</th>
              <th>Address</th>
              <th>Active Plan</th>
              <th>Birthdate</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {currentUsers.map((user, index) => (
              <tr key={user.id}>
                <td>{indexOfFirstUser + index + 1}</td>
                <td>
                  <div className="user-info">
                    <span className="user-name">{user.name}</span>
                  </div>
                </td>
                <td>{user.mobileNumber || '-'}</td>
                <td>{user.address || '-'}</td>
                <td>{user.activePlan || '...'}</td>
                <td>{user.birthday || user.birthDate || '-'}</td>
                <td>
                  <div className="action-buttons">
                    <button 
                      onClick={() => handleEditUser(user)}
                      className="btn-icon"
                      title="Edit User"
                    >
                      {TABLE_ICONS.EDIT}
                    </button>
                    <button 
                      onClick={() => handleDeleteClick(user)}
                      className="btn-icon"
                      title="Delete User"
                      disabled={deletingUserId === user.id}
                    >
                      {deletingUserId === user.id ? TABLE_ICONS.LOADING : TABLE_ICONS.DELETE}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button 
            onClick={() => paginate(currentPage - 1)} 
            disabled={currentPage === 1}
            className="pagination-btn pagination-nav"
          >
            Previous
          </button>
          
          {(() => {
            const getVisiblePages = () => {
              const maxVisible = 5;
              let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
              let end = Math.min(totalPages, start + maxVisible - 1);
              
              // Adjust start if we're near the end
              if (end - start + 1 < maxVisible) {
                start = Math.max(1, end - maxVisible + 1);
              }
              
              const pages = [];
              for (let i = start; i <= end; i++) {
                pages.push(i);
              }
              return pages;
            };
            
            return getVisiblePages().map((pageNum) => (
              <button
                key={pageNum}
                onClick={() => paginate(pageNum)}
                className={`pagination-btn pagination-number ${currentPage === pageNum ? 'active' : ''}`}
              >
                {pageNum}
              </button>
            ));
          })()}
          
          <button 
            onClick={() => paginate(currentPage + 1)} 
            disabled={currentPage === totalPages}
            className="pagination-btn pagination-nav"
          >
            Next
          </button>
        </div>
      )}

      {filteredUsers.length === 0 && !loading && (
        <div className="no-users">
          <div className="no-users-icon">👥</div>
          <h3>No Users Found</h3>
          <p>No users match your search criteria.</p>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="delete-confirm-modal">
            <div className="delete-modal-header">
              <h2>Confirm Delete</h2>
              <button 
                className="close-btn"
                onClick={handleDeleteCancel}
              >
                ✕
              </button>
            </div>
            
            <div className="delete-modal-content">
              <div className="warning-icon">
                {TABLE_ICONS.WARNING}
              </div>
              
              <div className="delete-message">
                <p>Are you sure you want to delete this user? This will also delete all uploads for this user.</p>
                <p className="warning-text">This action cannot be undone.</p>
              </div>
            </div>
            
            <div className="delete-modal-footer">
              <button 
                className="btn btn-secondary"
                onClick={handleDeleteCancel}
              >
                Cancel
              </button>
              <button 
                className="btn btn-danger"
                onClick={handleDeleteConfirm}
                disabled={deletingUserId === userToDelete?.id}
              >
                {deletingUserId === userToDelete?.id ? 'Deleting...' : 'Delete User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Snackbar */}
      {snackbar.show && (
        <Snackbar
          message={snackbar.message}
          type={snackbar.type}
          onClose={hideSnackbar}
        />
      )}
    </div>
  );
};

export default Users;
